import React, { useState, useEffect } from 'react';
import { Search, UserCheck, Mail, Loader, Plus, Building2, Phone, Award, Briefcase, FileText, Pencil } from 'lucide-react';
import { getAllDoctors, deleteDoctor, blockDoctor, unblockDoctor } from '../services/doctorService';
import { toast } from 'react-toastify';
import { DoctorProfile } from '../types';
import Modal from '../components/Common/Modal';
import DoctorFormModal from '../components/Doctor/DoctorFormModal';
import EditDoctorModal from '../components/Doctor/EditDoctorModal';

const Doctors: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [doctorToDelete, setDoctorToDelete] = useState<DoctorProfile | null>(null);
  const [doctorToBlock, setDoctorToBlock] = useState<DoctorProfile | null>(null);
  const [doctorToEdit, setDoctorToEdit] = useState<DoctorProfile | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorProfile | null>(null);
  const [doctors, setDoctors] = useState<DoctorProfile[]>([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);

  const fetchDoctorsFromAPI = async () => {
    setIsLoadingDoctors(true);

    try {
      const doctorsList = await getAllDoctors();
      setDoctors(doctorsList);
    } catch (error: any) {
      console.error("Failed to fetch doctors:", error);
      
      if (error.response) {
        const status = error.response.status;
        const errorMessage = error.response?.data?.message || error.message;
        
        if (status === 404) {
          toast.error("Doctors endpoint not found. Please ensure the backend API is running.");
        } else if (status === 403) {
          toast.error("You don't have permission to access doctor management.");
        } else if (status === 401) {
          toast.error("Authentication required. Please login again.");
        } else {
          toast.error(errorMessage || `Failed to load doctors (Status: ${status})`);
        }
      } else if (error.request) {
        toast.error("Unable to connect to server. Please check your connection.");
      } else {
        toast.error(error.message || "Failed to load doctors from server");
      }
      
      setDoctors([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  useEffect(() => {
    fetchDoctorsFromAPI();
  }, []);

  const filteredDoctors = doctors.filter((doctor) => {
    const name = doctor.name?.toLowerCase() || '';
    const email = doctor.email?.toLowerCase() || '';
    const licenseNumber = (doctor.licenseNumber || doctor.medicalLicenseNumber || '').toLowerCase();
    const regNumber = (doctor.medicalRegistrationNumber || '').toLowerCase();
    const department = doctor.department?.toLowerCase() || '';
    const specialization = doctor.specialization?.toLowerCase() || '';
    const hospital = doctor.hospitalName?.toLowerCase() || '';

    const term = searchTerm.toLowerCase();

    return (
      name.includes(term) ||
      email.includes(term) ||
      licenseNumber.includes(term) ||
      regNumber.includes(term) ||
      department.includes(term) ||
      specialization.includes(term) ||
      hospital.includes(term)
    );
  });

  const handleDelete = async () => {
    if (!doctorToDelete) return;

    try {
      await deleteDoctor(doctorToDelete.userId);
      toast.success('Doctor deleted successfully!');
      setDoctors(prev => prev.filter(d => d.userId !== doctorToDelete.userId));
      setIsDeleteModalOpen(false);
      setDoctorToDelete(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An error occurred while deleting the doctor';
      toast.error(errorMessage);
      console.error('Delete error:', error);
    }
  };

  const handleBlockUnblock = async () => {
    if (!doctorToBlock) return;

    try {
      const isBlocking = doctorToBlock.active;
      const updatedDoctor = isBlocking 
        ? await blockDoctor(doctorToBlock.userId)
        : await unblockDoctor(doctorToBlock.userId);

      const action = isBlocking ? 'blocked' : 'unblocked';
      toast.success(`Doctor ${action} successfully!`);
      
      setDoctors(prev => prev.map(d => 
        d.userId === doctorToBlock.userId 
          ? updatedDoctor
          : d
      ));
      
      setIsBlockModalOpen(false);
      setDoctorToBlock(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || `An error occurred while ${doctorToBlock.active ? 'blocking' : 'unblocking'} the doctor`;
      toast.error(errorMessage);
      console.error('Block/Unblock error:', error);
    }
  };

  const handleCardClick = (doctor: DoctorProfile) => {
    setSelectedDoctor(doctor);
    setIsProfileModalOpen(true);
  };

  const handleEditClick = (doctor: DoctorProfile) => {
    setDoctorToEdit(doctor);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Doctor Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Doctor Onboarding</h1>
          <p className="text-gray-600">Register and manage hospital doctors on the platform</p>
        </div>

        <button
          onClick={() => setIsFormModalOpen(true)}
          className="btn-primary flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
        >
          <Plus className="h-5 w-5" />
          <span>Add Doctor</span>
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, department, hospital, specialization, or license/registration number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoadingDoctors && (
        <div className="card text-center py-12">
          <Loader className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading doctors...</h3>
          <p className="text-gray-600">Please wait while we fetch the doctor records.</p>
        </div>
      )}

      {/* Doctors Grid */}
      {!isLoadingDoctors && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDoctors.map((doctor) => (
            <div
              key={doctor.userId}
              className="card cursor-pointer hover:shadow-lg transition-shadow duration-200 border border-gray-100 flex flex-col justify-between"
              onClick={() => handleCardClick(doctor)}
            >
              <div>
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <UserCheck className="h-6 w-6 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate" title={doctor.name}>{doctor.name}</h3>
                      {doctor.specialization && (
                        <p className="text-xs font-medium text-primary-700 truncate" title={doctor.specialization}>
                          {doctor.specialization}
                        </p>
                      )}
                      {doctor.department && (
                        <p className="text-xs text-gray-500 truncate" title={doctor.department}>
                          {doctor.department}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Status Badge */}
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                      doctor.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {doctor.active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="space-y-2 text-sm border-t border-gray-100 pt-3">
                  {doctor.hospitalName && (
                    <div className="flex items-center space-x-2 text-gray-700 min-w-0">
                      <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="font-medium truncate" title={doctor.hospitalName}>
                        {doctor.hospitalName}
                      </span>
                    </div>
                  )}

                  {doctor.email && (
                    <div className="flex items-center space-x-2 text-gray-600 min-w-0">
                      <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate text-xs" title={doctor.email}>{doctor.email}</span>
                    </div>
                  )}

                  {doctor.phoneNumber && (
                    <div className="flex items-center space-x-2 text-gray-600 min-w-0">
                      <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate text-xs">{doctor.phoneNumber}</span>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 text-gray-600 text-xs pt-1">
                    <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">
                      Reg: <span className="font-medium">{doctor.medicalRegistrationNumber || 'N/A'}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(doctor);
                  }}
                  className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors duration-200 bg-[#0F9D9A] hover:bg-[#0C8582] text-white flex items-center gap-1"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDoctorToBlock(doctor);
                    setIsBlockModalOpen(true);
                  }}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors duration-200 ${
                    doctor.active
                      ? 'bg-[#D28C78] hover:bg-[#B97866] text-white'
                      : 'bg-[#0F9D9A] hover:bg-[#0C8582] text-white'
                  }`}
                >
                  {doctor.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDoctorToDelete(doctor);
                    setIsDeleteModalOpen(true);
                  }}
                  className="btn-danger text-xs px-3 py-1.5"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoadingDoctors && filteredDoctors.length === 0 && (
        <div className="card text-center py-16">
          <UserCheck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No doctors found</h3>
          <p className="text-gray-500 text-sm max-w-sm mx-auto mb-6">
            {searchTerm
              ? 'No doctor records match your search query. Try adjusting your search term.'
              : 'There are currently no doctors onboarded on the platform.'}
          </p>
          <button
            onClick={() => setIsFormModalOpen(true)}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Onboard First Doctor</span>
          </button>
        </div>
      )}

      {/* Doctor Registration Form Modal */}
      <DoctorFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSuccess={fetchDoctorsFromAPI}
      />

      {/* Edit Doctor Modal */}
      <EditDoctorModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setDoctorToEdit(null);
        }}
        onSuccess={fetchDoctorsFromAPI}
        doctor={doctorToEdit}
      />

      {/* Profile Detail Modal */}
      <Modal
        title="Doctor Details"
        isOpen={isProfileModalOpen}
        onClose={() => {
          setIsProfileModalOpen(false);
          setSelectedDoctor(null);
        }}
        widthClassName="max-w-xl"
      >
        {selectedDoctor && (
          <div className="space-y-6">
            {/* Header Profile Info */}
            <div className="flex items-center space-x-4 border-b border-gray-200 pb-4">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCheck className="h-8 w-8 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedDoctor.name}</h2>
                <p className="text-sm text-primary-700 font-medium">{selectedDoctor.specialization || 'Doctor'}</p>
                <p className="text-xs text-gray-500">{selectedDoctor.department || 'General Department'}</p>
              </div>
            </div>

            {/* Grid of Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Email</span>
                <span className="text-gray-900">{selectedDoctor.email || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Phone Number</span>
                <span className="text-gray-900">{selectedDoctor.phoneNumber || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Hospital</span>
                <span className="text-gray-900 font-medium">{selectedDoctor.hospitalName || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Department</span>
                <span className="text-gray-900">{selectedDoctor.department || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Med Registration No.</span>
                <span className="text-gray-900 font-mono">{selectedDoctor.medicalRegistrationNumber || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">License Number</span>
                <span className="text-gray-900 font-mono">
                  {selectedDoctor.licenseNumber || selectedDoctor.medicalLicenseNumber || 'N/A'}
                </span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Qualification</span>
                <span className="text-gray-900">{selectedDoctor.qualification || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Years of Experience</span>
                <span className="text-gray-900">
                  {selectedDoctor.yearsOfExperience !== null && selectedDoctor.yearsOfExperience !== undefined
                    ? `${selectedDoctor.yearsOfExperience} Years`
                    : 'N/A'}
                </span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Status</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mt-1 ${
                    selectedDoctor.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {selectedDoctor.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Edit Button */}
            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={() => {
                  setIsProfileModalOpen(false);
                  setSelectedDoctor(null);
                  handleEditClick(selectedDoctor);
                }}
                className="w-full btn-primary text-sm flex items-center justify-center gap-2"
              >
                <Pencil className="h-4 w-4" />
                Edit Doctor Profile
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Block/Unblock Confirmation Modal */}
      {isBlockModalOpen && doctorToBlock && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-1/2 transform -translate-y-1/2 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {doctorToBlock.active ? 'Deactivate Doctor' : 'Activate Doctor'}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to {doctorToBlock.active ? 'deactivate' : 'activate'}{' '}
                <span className="font-semibold text-gray-900">{doctorToBlock.name}</span>?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsBlockModalOpen(false);
                    setDoctorToBlock(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBlockUnblock}
                  className={`btn-primary ${
                    doctorToBlock.active 
                      ? 'bg-[#D28C78] hover:bg-[#B97866]' 
                      : 'bg-[#0F9D9A] hover:bg-[#0C8582]'
                  }`}
                >
                  {doctorToBlock.active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && doctorToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-1/2 transform -translate-y-1/2 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Deletion</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-gray-900">{doctorToDelete.name}</span>?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setDoctorToDelete(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="btn-primary bg-[#D96F91] hover:bg-[#C75C7E]"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Doctors;
